import React, { useState, useEffect } from 'react';
import { X, Globe, CheckCircle, AlertTriangle, Clock, RefreshCw, Copy, Check, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminApiClient } from '../../lib/api';
import { CustomDomainState, DomainDnsRecord } from './types';

interface CustomDomainModalProps {
    isOpen: boolean;
    onClose: () => void;
    templeId: string;
    templeName: string;
}

export const CustomDomainModal: React.FC<CustomDomainModalProps> = ({
    isOpen,
    onClose,
    templeId,
    templeName,
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [domainInput, setDomainInput] = useState<string>('');
    const [state, setState] = useState<CustomDomainState | null>(null);
    const [copied, setCopied] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen && templeId) {
            loadDomainData();
        }
    }, [isOpen, templeId]);

    const loadDomainData = async () => {
        try {
            setLoading(true);
            const res = await adminApiClient.getTempleCustomDomain(templeId);
            if (res.success && res.data) {
                setState(res.data);
                setDomainInput(res.data.customDomain || '');
            } else {
                toast.error(res.message || 'Failed to load custom domain data');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error loading custom domain');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDomain = async () => {
        try {
            setSaving(true);
            const res = await adminApiClient.updateTempleCustomDomain(templeId, domainInput.trim());
            if (res.success && res.data) {
                setState(res.data);
                setDomainInput(res.data.customDomain || '');
                toast.success(res.message || 'Custom domain updated successfully');
            } else {
                toast.error(res.message || 'Failed to update custom domain');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error updating custom domain');
        } finally {
            setSaving(false);
        }
    };

    const handleClearDomain = async () => {
        if (!window.confirm(`Are you sure you want to remove the custom domain for ${templeName}?`)) return;
        try {
            setSaving(true);
            const res = await adminApiClient.updateTempleCustomDomain(templeId, '');
            if (res.success && res.data) {
                setState(res.data);
                setDomainInput('');
                toast.success('Custom domain removed successfully');
            } else {
                toast.error(res.message || 'Failed to remove custom domain');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error removing custom domain');
        } finally {
            setSaving(false);
        }
    };

    const handleRefreshStatus = async () => {
        try {
            setSaving(true);
            const res = await adminApiClient.refreshTempleCustomDomain(templeId);
            if (res.success && res.data) {
                setState(res.data);
                toast.success(res.message || 'Domain status refreshed');
            } else {
                toast.error(res.message || 'Failed to refresh status');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error refreshing status');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleSetting = async (key: 'customDomainEnabled' | 'canTempleAdminManageDomain' | 'marketplaceEnabled') => {
        if (!state) return;
        const updatedVal = !state[key];
        try {
            setSaving(true);
            const res = await adminApiClient.updateTempleCustomDomainSettings(templeId, {
                [key]: updatedVal,
            });
            if (res.success && res.data) {
                setState(res.data);
                toast.success('Setting updated successfully');
            } else {
                toast.error(res.message || 'Failed to update setting');
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Error updating setting');
        } finally {
            setSaving(false);
        }
    };

    const handleCopyDnsRecords = () => {
        if (!state?.provisioning?.records || state.provisioning.records.length === 0) return;
        const text = state.provisioning.records.map((r: DomainDnsRecord) =>
            `Purpose: ${r.purpose}\nType: ${r.type}\nHost: ${r.host}\nValue: ${r.value}${r.note ? `\nNote: ${r.note}` : ''}\n`
        ).join('\n---\n\n');

        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('DNS records copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
    };

    if (!isOpen) return null;

    const records = state?.provisioning?.records || [];

    const renderStatusBadge = (status: string) => {
        switch (status) {
            case 'active':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                        <CheckCircle size={14} className="text-green-600" /> Active & Verified
                    </span>
                );
            case 'pending_dns':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        <Clock size={14} className="text-amber-600" /> Pending DNS Setup
                    </span>
                );
            case 'pending_cert':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        <Clock size={14} className="text-blue-600" /> Provisioning SSL Certificate
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                        <AlertTriangle size={14} className="text-red-600" /> Verification Failed
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                        None / Not Configured
                    </span>
                );
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-100 overflow-hidden transform transition-all my-8">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-orange-100 text-orange-600">
                            <Globe size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900">Custom Domain Governance</h3>
                            <p className="text-xs text-gray-500 font-medium">Manage domain masking for <strong className="text-gray-800">{templeName}</strong></p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-500 gap-3">
                            <RefreshCw className="animate-spin text-orange-500" size={28} />
                            <p className="text-sm">Loading domain details...</p>
                        </div>
                    ) : (
                        <>
                            {/* Super Admin Feature Governance Section */}
                            <div className="bg-orange-50/40 rounded-xl p-4 border border-orange-100 space-y-4">
                                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                                    <Shield size={16} className="text-orange-600" />
                                    <span>Platform Governance Controls</span>
                                </div>

                                <div className="space-y-3 divide-y divide-orange-100/60 text-sm">
                                    {/* Toggle 1: Feature Enable/Disable */}
                                    <div className="flex items-center justify-between pt-2">
                                        <div>
                                            <p className="font-medium text-gray-800">Enable Custom Domain Feature</p>
                                            <p className="text-xs text-gray-500">Allow this temple to have an active branded custom domain</p>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={saving}
                                            onClick={() => handleToggleSetting('customDomainEnabled')}
                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${state?.customDomainEnabled ? 'bg-orange-600' : 'bg-gray-300'}`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${state?.customDomainEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                                            />
                                        </button>
                                    </div>

                                    {/* Toggle 2: Temple Admin Self-Management */}
                                    <div className="flex items-center justify-between pt-3">
                                        <div>
                                            <p className="font-medium text-gray-800">Allow Temple Admin to Self-Manage</p>
                                            <p className="text-xs text-gray-500">Enable Domain tab in temple dashboard so temple admin can edit their domain</p>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={saving}
                                            onClick={() => handleToggleSetting('canTempleAdminManageDomain')}
                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${state?.canTempleAdminManageDomain ? 'bg-orange-600' : 'bg-gray-300'}`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${state?.canTempleAdminManageDomain ? 'translate-x-5' : 'translate-x-0'}`}
                                            />
                                        </button>
                                    </div>

                                    {/* Toggle 3: Marketplace Visibility */}
                                    <div className="flex items-center justify-between pt-3">
                                        <div>
                                            <p className="font-medium text-gray-800">Marketplace Directory Listing</p>
                                            <p className="text-xs text-gray-500">Show this temple on public funddharma.com directory</p>
                                        </div>
                                        <button
                                            type="button"
                                            disabled={saving}
                                            onClick={() => handleToggleSetting('marketplaceEnabled')}
                                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${state?.marketplaceEnabled ? 'bg-green-600' : 'bg-gray-300'}`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${state?.marketplaceEnabled ? 'translate-x-5' : 'translate-x-0'}`}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Setup on Behalf of Temple */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <label className="block text-sm font-semibold text-gray-900">
                                        Custom Domain
                                    </label>
                                    {state && renderStatusBadge(state.customDomainStatus)}
                                </div>

                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={domainInput}
                                        onChange={(e) => setDomainInput(e.target.value)}
                                        placeholder="e.g. saibaba.org or temple.org"
                                        disabled={saving}
                                        className="flex-1 px-3.5 py-2 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSaveDomain}
                                        disabled={saving || !domainInput.trim()}
                                        className="px-4 py-2 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium transition-colors disabled:opacity-50"
                                    >
                                        {saving ? 'Saving...' : 'Save Domain'}
                                    </button>
                                    {state?.customDomain && (
                                        <button
                                            type="button"
                                            onClick={handleClearDomain}
                                            disabled={saving}
                                            className="px-3 py-2 rounded-xl border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium transition-colors disabled:opacity-50"
                                        >
                                            Remove
                                        </button>
                                    )}
                                </div>

                                {state?.customDomain && (
                                    <div className="flex items-center justify-between text-xs text-gray-500">
                                        <span>Configured aliases: <strong className="font-mono text-gray-700">{state.domainAliases.join(', ')}</strong></span>
                                        <button
                                            type="button"
                                            onClick={handleRefreshStatus}
                                            disabled={saving}
                                            className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                                        >
                                            <RefreshCw size={13} className={saving ? 'animate-spin' : ''} /> Check DNS Status
                                        </button>
                                    </div>
                                )}

                                {state?.provisioning?.failureReason && (
                                    <div className="p-3 bg-red-50 rounded-xl border border-red-200 text-xs text-red-700 flex items-start gap-2">
                                        <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                                        <span>{state.provisioning.failureReason}</span>
                                    </div>
                                )}
                            </div>

                            {/* DNS Instructions Table */}
                            {records.length > 0 && (
                                <div className="space-y-3 pt-2 border-t border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-900">Required DNS Records</h4>
                                            <p className="text-xs text-gray-500">Provide these records to the temple for registrar setup</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleCopyDnsRecords}
                                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                        >
                                            {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                                            {copied ? 'Copied' : 'Copy All Records'}
                                        </button>
                                    </div>

                                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                                            <thead className="bg-gray-50 text-gray-600 font-medium">
                                                <tr>
                                                    <th className="py-2.5 px-3 text-left">Purpose</th>
                                                    <th className="py-2.5 px-3 text-left">Type</th>
                                                    <th className="py-2.5 px-3 text-left">Host / Name</th>
                                                    <th className="py-2.5 px-3 text-left">Value / Target</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 bg-white">
                                                {records.map((r, i) => (
                                                    <tr key={i} className="hover:bg-gray-50/50">
                                                        <td className="py-2 px-3 capitalize font-medium text-gray-700">{r.purpose}</td>
                                                        <td className="py-2 px-3 font-mono font-bold text-orange-600">{r.type}</td>
                                                        <td className="py-2 px-3 font-mono text-gray-800 break-all">{r.host}</td>
                                                        <td className="py-2 px-3 font-mono text-gray-800 break-all">
                                                            {r.value}
                                                            {r.note && <span className="block text-[11px] text-gray-500 font-sans mt-0.5">{r.note}</span>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-xl bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-medium transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

